import { useClimateMetrics, Metric, useClimateHazards } from '../hooks/analytics'
import { useLocationGroups, useLocations } from '../hooks/locations'
import { api } from 'balkerne-core/api'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useHistory } from 'react-router-dom'
import Page from '../components/Page'

// -------------------- Types -------------------- //

type EmissionTier = 'low' | 'moderate' | 'high'

interface Hazard {
  id: number
  name: string // e.g. 'fld'
  display_name: string // e.g. 'Flood'
}

// Backend export request types
interface MetricExportConfig {
  metric_id?: number
  metric_key?: { hazard: string; code: string }
  use_defaults: boolean
  facet_filters?: Record<string, unknown> | null
}

interface ExportMetricsParams {
  property_ids?: number[]
  group_id?: number
  metrics: MetricExportConfig[]
  emission_tiers?: EmissionTier[] | null
}

type MetricSelection = {
  id: number
  selected: boolean
  useDefaults: boolean // hidden toggle: true by default; becomes false when user saves facet overrides
  facetValues: Record<string, unknown> // when useDefaults is true, this should be {} so we don't send overrides
}

function coerceDefaultsForMulti(metric: Metric): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  const schema = metric.facet_schema || {}
  const defs = metric.facet_defaults || {}
  for (const [key, def] of Object.entries(schema)) {
    const d = (defs as any)[key]
    if (def.type === 'enum') {
      // allow multiple values: coerce single to array
      if (Array.isArray(d)) out[key] = d
      else if (typeof d === 'string') out[key] = [d]
      else out[key] = [] // nothing selected by default if metric has no default
    } else {
      // keep as-is for bool/text
      if (d !== undefined) out[key] = d
    }
  }
  return out
}

function summarizeFacetOverrides(metric: Metric, values: Record<string, unknown>): string[] {
  const bits: string[] = []
  const schema = metric.facet_schema || {}
  for (const [k, def] of Object.entries(schema)) {
    const v = (values as any)[k]
    if (v === undefined) continue
    if (def.type === 'enum') {
      const arr = Array.isArray(v) ? v : v != null ? [String(v)] : []
      if (arr.length) bits.push(`${k}: ${arr.join(', ')}`)
    } else if (def.type === 'bool') {
      bits.push(`${k}: ${v ? 'true' : 'false'}`)
    } else {
      if (String(v).length) bits.push(`${k}: ${v}`)
    }
  }
  return bits
}

function cleanseFacetFiltersForPayload(
  values: Record<string, unknown>,
  metric: Metric,
): Record<string, unknown> | undefined {
  // Remove empty arrays/empties so backend doesn't get noisy payloads
  const cleaned: Record<string, unknown> = {}
  const schema = metric.facet_schema || {}
  for (const [k, def] of Object.entries(schema)) {
    const v = (values as any)[k]
    if (v === undefined || v === null) continue
    if (def.type === 'enum') {
      const arr = Array.isArray(v) ? v : [v]
      if (arr.length) cleaned[k] = arr
    } else if (def.type === 'text') {
      if (String(v).trim().length) cleaned[k] = v
    } else if (def.type === 'bool') {
      cleaned[k] = v
    }
  }
  return Object.keys(cleaned).length ? cleaned : undefined
}

function FacetDialog({
  open,
  metric,
  initialValues,
  onCancel,
  onSave,
  onClear,
}: {
  open: boolean
  metric: Metric
  initialValues: Record<string, unknown>
  onCancel: () => void
  onSave: (nextValues: Record<string, unknown>) => void // caller sets useDefaults=false on save
  onClear: () => void // caller sets useDefaults=true and clears values
}) {
  const [values, setValues] = useState<Record<string, unknown>>({})

  useEffect(() => {
    setValues(initialValues)
  }, [initialValues, open])

  const schema = metric.facet_schema || {}

  const handleField = (key: string, value: unknown) => setValues(v => ({ ...v, [key]: value }))

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Configure facets — {metric.label}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {Object.entries(schema).length === 0 && (
            <Alert severity="info">This metric has no configurable facets.</Alert>
          )}
          {Object.entries(schema).map(([key, def]) => {
            const v = (values as any)[key]
            if (def.type === 'enum') {
              const arr = Array.isArray(v) ? (v as string[]) : v != null ? [String(v)] : []
              return (
                <FormControl key={key} fullWidth>
                  <InputLabel id={`${metric.id}-${key}-label`}>{key}</InputLabel>
                  <Select
                    multiple
                    labelId={`${metric.id}-${key}-label`}
                    label={key}
                    value={arr}
                    onChange={e => handleField(key, e.target.value as string[])}
                    renderValue={selected => (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {(selected as string[]).map(opt => (
                          <Chip key={opt} size="small" label={opt} sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </Stack>
                    )}>
                    {(def.values || []).map(opt => (
                      <MenuItem key={opt} value={opt}>
                        <Checkbox checked={arr.includes(opt)} />
                        <Typography sx={{ ml: 1 }}>{opt}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )
            }
            if (def.type === 'bool') {
              return (
                <FormControlLabel
                  key={key}
                  control={<Checkbox checked={Boolean(v)} onChange={e => handleField(key, e.target.checked)} />}
                  label={key}
                />
              )
            }
            // text
            return (
              <TextField
                key={key}
                fullWidth
                label={key}
                value={(v as string) ?? ''}
                onChange={e => handleField(key, e.target.value)}
              />
            )
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClear} color="inherit">
          Clear overrides
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(values)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function MetricRow({
  metric,
  selection,
  onChange,
}: {
  metric: Metric
  selection: MetricSelection
  onChange: (next: MetricSelection) => void
}) {
  const [open, setOpen] = useState(false)
  const overridesSummary = useMemo(
    () => (!selection.useDefaults ? summarizeFacetOverrides(metric, selection.facetValues) : []),
    [selection.useDefaults, selection.facetValues, metric],
  )

  const handleOpen = () => {
    // If we are using defaults and have no local values, seed with defaults (coerced for multi)
    if (selection.useDefaults) {
      onChange({ ...selection, facetValues: coerceDefaultsForMulti(metric) })
    }
    setOpen(true)
  }

  const handleSave = (vals: Record<string, unknown>) => {
    onChange({ ...selection, useDefaults: false, facetValues: vals })
    setOpen(false)
  }

  const handleClear = () => {
    // revert to defaults mode; clear facet values so payload won't include overrides
    onChange({ ...selection, useDefaults: true, facetValues: {} })
    setOpen(false)
  }

  return (
    <Box
      key={metric.id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1,
        px: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
      <Checkbox
        checked={selection.selected}
        onChange={e => onChange({ ...selection, selected: e.target.checked })}
        sx={{ mr: 1 }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1" noWrap>
          {metric.label}
          <Typography component="span" variant="body2" color="text.secondary">
            {' '}
            ({metric.hazard}.{metric.code}
            {metric.unit ? ` • ${metric.unit}` : ''})
          </Typography>
        </Typography>
        {!selection.useDefaults && overridesSummary.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
            {overridesSummary.map(t => (
              <Chip key={t} size="small" variant="outlined" label={t} sx={{ mr: 0.5, mb: 0.5 }} />
            ))}
          </Stack>
        )}
      </Box>

      <Button size="small" color="inherit" sx={{ color: 'text.secondary', minWidth: 'auto' }} onClick={handleOpen}>
        Configure
      </Button>

      <FacetDialog
        open={open}
        metric={metric}
        initialValues={selection.useDefaults ? coerceDefaultsForMulti(metric) : selection.facetValues}
        onCancel={() => setOpen(false)}
        onSave={handleSave}
        onClear={handleClear}
      />
    </Box>
  )
}

function MetricsExportPage() {
  const { enqueueSnackbar } = useSnackbar()
  const history = useHistory()

  const { data: hazards, isLoading: hzLoading, error: hzError } = useClimateHazards()
  const { data: metrics, isLoading: mLoading, error: mError } = useClimateMetrics()
  const { data: locationGroups, isLoading: groupsLoading } = useLocationGroups()
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)
  const { data: locations, isLoading: locLoading } = useLocations(selectedGroupId ? { group_id: selectedGroupId } : {})

  const [exportAllProps, setExportAllProps] = useState<boolean>(true) // default ON
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([])
  const [tiers, setTiers] = useState<EmissionTier[]>(['low', 'moderate', 'high'])
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [submitError, setSubmitError] = useState<string>('')

  // metricId -> selection
  const [selections, setSelections] = useState<Record<number, MetricSelection>>({})

  // Initialize selections when metrics arrive
  useEffect(() => {
    if (!metrics) return
    setSelections(prev => {
      const next: Record<number, MetricSelection> = { ...prev }
      metrics.forEach(m => {
        if (!next[m.id]) {
          next[m.id] = {
            id: m.id,
            selected: false,
            useDefaults: true,
            facetValues: {}, // keep empty until user configures; defaults are injected in dialog
          }
        }
      })
      return next
    })
  }, [metrics])

  useEffect(() => {
    setSelectedPropertyIds(prev => prev.filter(id => (locations || []).some((location: any) => location.id === id)))
  }, [locations])

  const hazardLookup = useMemo(() => {
    const map = new Map<string, Hazard>()
    ;(hazards || []).forEach(h => map.set(h.name, h))
    return map
  }, [hazards])

  const metricsByHazard = useMemo(() => {
    const groups = new Map<string, Metric[]>()
    ;(metrics || []).forEach(m => {
      const list = groups.get(m.hazard) || []
      list.push(m)
      groups.set(m.hazard, list)
    })
    // sort metrics by label within each hazard
    for (const [k, list] of groups) {
      list.sort((a, b) => a.label.localeCompare(b.label))
      groups.set(k, list)
    }
    return groups
  }, [metrics])

  const selectedMetrics: Metric[] = useMemo(() => {
    if (!metrics) return []
    return metrics.filter(m => selections[m.id]?.selected)
  }, [metrics, selections])
  const selectedMetricCount = selectedMetrics.length
  const selectedPropertyCount = useMemo(
    () => (exportAllProps ? locations?.length || 0 : selectedPropertyIds.length),
    [exportAllProps, locations, selectedPropertyIds],
  )
  const exportDisabled = submitState === 'submitting' || selectedMetricCount === 0

  const handleSelectionChange = (metricId: number, next: MetricSelection) => {
    setSelections(s => ({ ...s, [metricId]: next }))
  }

  function extractFilename(disposition?: string): string | null {
    if (!disposition) return null
    // typical: attachment; filename="file.xlsx" OR filename*=UTF-8''file.xlsx
    const filenameStar = disposition.match(/filename\*=UTF-8''([^;]+)/i)
    if (filenameStar) return decodeURIComponent(filenameStar[1])
    const filename = disposition.match(/filename="?([^";]+)"?/i)
    return filename ? filename[1] : null
  }

  const handleExport = async () => {
    const allPropsIds = (locations || []).map((p: any) => p.id)
    const propertyIdsForPayload = exportAllProps ? allPropsIds : selectedPropertyIds

    if (!propertyIdsForPayload.length) {
      setSubmitError('No properties available to export.')
      setSubmitState('error')
      return
    }
    if (!selectedMetrics.length) {
      setSubmitError('Please select at least one metric to export.')
      setSubmitState('error')
      return
    }

    const metricsConfig: MetricExportConfig[] = selectedMetrics.map(m => {
      const sel = selections[m.id]
      const filters = !sel.useDefaults ? cleanseFacetFiltersForPayload(sel.facetValues, m) : undefined
      return {
        metric_id: m.id,
        use_defaults: sel.useDefaults,
        facet_filters: filters,
      }
    })

    const payload: ExportMetricsParams = {
      ...(exportAllProps && selectedGroupId ? { group_id: selectedGroupId } : { property_ids: propertyIdsForPayload }),
      metrics: metricsConfig,
      emission_tiers: tiers.length ? tiers : undefined,
    }

    try {
      setSubmitState('submitting')
      setSubmitError('')
      // Request may return a file (xlsx) OR a JSON envelope. Ask for blob and branch by content-type.
      const response = await api.post('/analytics/cc/metrics/export', payload, {
        responseType: 'blob',
        validateStatus: () => true, // we'll handle error statuses manually so we can parse JSON body
      })

      const contentType = String(response.headers['content-type'] || '').toLowerCase()
      const isJson = contentType.includes('application/json') || contentType.includes('text/json')

      if (isJson) {
        // Parse JSON error or success message from blob
        const text = await (response.data as Blob).text()
        const data = text ? JSON.parse(text) : {}
        if (response.status >= 200 && response.status < 300) {
          setSubmitState('success')
          enqueueSnackbar('Export request submitted.', { variant: 'success' })
        } else {
          const message = data?.detail || data?.message || `Export failed with status ${response.status}`
          setSubmitError(message)
          setSubmitState('error')
          enqueueSnackbar(message, { variant: 'error' })
        }
        return
      }

      // Assume it's a downloadable file
      if (response.status >= 200 && response.status < 300) {
        const blob: Blob = response.data as Blob
        const disposition = String(response.headers['content-disposition'] || '')
        const filename =
          extractFilename(disposition) ||
          (() => {
            const today = new Date()
            const yyyy = today.getFullYear()
            const mm = String(today.getMonth() + 1).padStart(2, '0')
            const dd = String(today.getDate()).padStart(2, '0')
            return `climate_analytics_export_${yyyy}-${mm}-${dd}.xlsx`
          })()

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        setSubmitState('success')
        enqueueSnackbar('Export successful! Downloading file…', { variant: 'success' })
      } else {
        // Non-JSON error with blob body (e.g., HTML). Try to show a generic error.
        setSubmitError(`Export failed with status ${response.status}`)
        setSubmitState('error')
        enqueueSnackbar('Export failed. Please try again.', { variant: 'error' })
      }
    } catch (err: any) {
      setSubmitError(err?.response?.data?.detail || err?.message || 'Export failed')
      setSubmitState('error')
      enqueueSnackbar('Export failed. Please try again.', { variant: 'error' })
    }
  }
  const emissionOptions: EmissionTier[] = ['high', 'moderate', 'low']
  const toggleTier = (tier: EmissionTier) =>
    setTiers(prev => (prev.includes(tier) ? prev.filter(t => t !== tier) : [...prev, tier]))
  const loadingAny = hzLoading || mLoading || locLoading || groupsLoading

  const selectedGroup = useMemo(
    () => (locationGroups || []).find((group: any) => group.id === selectedGroupId) ?? null,
    [locationGroups, selectedGroupId],
  )

  const handleGroupChange = (_: unknown, value: any) => {
    setSelectedGroupId(value?.id ?? null)
    setSelectedPropertyIds([])
  }

  const handleClearSelections = () => {
    setSelectedGroupId(null)
    setSelectedPropertyIds([])
    setExportAllProps(true)
    setTiers(['low', 'moderate', 'high'])
    setSelections(s => {
      const next = { ...s }
      Object.keys(next).forEach(k => {
        const key = Number(k)
        next[key] = {
          ...next[key],
          selected: false,
          useDefaults: true,
          facetValues: {},
        }
      })
      return next
    })
    setSubmitState('idle')
    setSubmitError('')
  }

  return (
    <Page title="Climate Analytics Export" back>
      <Box p={1}>
        <Stack spacing={3}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h6">Configure Export</Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Chip
                label={`${selectedMetricCount} metric${selectedMetricCount === 1 ? '' : 's'} selected`}
                color={selectedMetricCount ? 'primary' : 'default'}
                variant={selectedMetricCount ? 'filled' : 'outlined'}
              />
              <Button variant="contained" onClick={handleExport} disabled={exportDisabled}>
                {submitState === 'submitting' ? 'Exporting…' : 'Export'}
              </Button>
              <Button variant="text" onClick={handleClearSelections}>
                Clear selection
              </Button>
            </Stack>
          </Stack>

          {loadingAny && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2">Loading…</Typography>
            </Stack>
          )}

          {!loadingAny && !hazards?.length && !mError && (
            <Alert severity="info">No climate hazards or metrics available.</Alert>
          )}

          {submitState === 'error' && <Alert severity="error">{submitError}</Alert>}
          {submitState === 'success' && <Alert severity="success">Export request submitted.</Alert>}

          <Card variant="outlined">
            <CardHeader title="Scope" />
            <CardContent>
              <Stack spacing={2.5}>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Properties
                    </Typography>
                    <Chip
                      size="small"
                      label={`${selectedPropertyCount} selected`}
                      color={selectedPropertyCount ? 'primary' : 'default'}
                      variant={selectedPropertyCount ? 'filled' : 'outlined'}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Pick a property portfolio to select locations from. No group defaults to the whole portfolio.
                  </Typography>
                  <Stack spacing={1.5} direction="column">
                    <Autocomplete
                      options={locationGroups || []}
                      getOptionLabel={o => o.name}
                      value={selectedGroup}
                      onChange={handleGroupChange}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={params => <TextField {...params} label="Property group" placeholder="All" />}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id} sx={{ display: 'flex', width: '100%' }}>
                          <Typography variant="body1" sx={{ flex: 1 }}>
                            {option.name}
                          </Typography>
                          {option.property_customer_reference && (
                            <Typography variant="caption" color="text.secondary">
                              {option.property_customer_reference}
                            </Typography>
                          )}
                        </Box>
                      )}
                    />
                    <hr />
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FormGroup sx={{ m: 0, flexShrink: 0 }}>
                        <FormControlLabel
                          control={<Switch checked={exportAllProps} onChange={(_, v) => setExportAllProps(v)} />}
                          label="Include all"
                        />
                      </FormGroup>
                      <Autocomplete
                        multiple
                        fullWidth
                        disableCloseOnSelect
                        options={(locations || []).map((p: any) => ({ id: p.id, name: p.name }))}
                        getOptionLabel={o => o.name}
                        value={(locations || [])
                          .filter((p: any) => selectedPropertyIds.includes(p.id))
                          .map((p: any) => ({ id: p.id, name: p.name }))}
                        onChange={(_, vals) => setSelectedPropertyIds(vals.map(v => v.id))}
                        renderInput={params => (
                          <TextField {...params} label="Properties" placeholder="Select properties" />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip variant="outlined" label={option.name} {...getTagProps({ index })} key={option.id} />
                          ))
                        }
                        disabled={exportAllProps}
                      />
                    </Stack>
                  </Stack>
                </Box>

                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                    Emission tiers
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Select the emissions pathways to include in the export.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {emissionOptions.map(t => {
                      const selected = tiers.includes(t)
                      return (
                        <Chip
                          key={t}
                          label={t.charAt(0).toUpperCase() + t.slice(1)}
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          onClick={() => toggleTier(t)}
                          sx={{ minWidth: 96 }}
                        />
                      )
                    })}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader title="Metrics" />
            <CardContent sx={{ p: 0 }}>
              {(Array.from(metricsByHazard.entries()) || []).map(([hazardKey, list]) => (
                <Accordion key={hazardKey}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">{hazardLookup.get(hazardKey)?.display_name || hazardKey}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <Box>
                      {list.map(metric => (
                        <MetricRow
                          key={metric.id}
                          metric={metric}
                          selection={
                            selections[metric.id] || {
                              id: metric.id,
                              selected: false,
                              useDefaults: true,
                              facetValues: {},
                            }
                          }
                          onChange={next => handleSelectionChange(metric.id, next)}
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </Page>
  )
}

export default MetricsExportPage
